import { useSelector } from 'react-redux';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FIELD_LABEL } from './fileCardStyles';
import { selectV2 } from '../v2Upload.slice';
import { STEP_LABELS } from '../stepLabels';
import StepNavigation from './StepNavigation';

// fallow-ignore-next-line complexity
function RailRow({
  number,
  label,
  note,
  isNow,
  isDone,
}: {
  number: number;
  label: string;
  note: string;
  isNow: boolean;
  isDone: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm border',
        isNow && 'bg-accent border-primary/30 text-foreground font-medium',
        !isNow && isDone && 'border-transparent text-foreground',
        !isNow && !isDone && 'border-transparent text-muted-foreground'
      )}
    >
      <span
        className={cn(
          'w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] flex-none',
          isNow && 'bg-primary text-white',
          !isNow && isDone && 'bg-green-500 text-white',
          !isNow && !isDone && 'border border-muted-foreground/40'
        )}
      >
        {isDone && !isNow ? <Check className="w-2.5 h-2.5" /> : number}
      </span>
      <span className="flex-1">{label}</span>
      <span className="text-[10px] text-muted-foreground">{note}</span>
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function ProjectProgress() {
  // maxStep, not step: it counts the steps whose sync has actually run, so
  // walking back to fix a field doesn't read as losing progress.
  const { maxStep, mode, multiStage, workpieceIndex, childIds, postId } =
    useSelector(selectV2);
  // Choose Type is not work, so it is outside the count — including it would
  // read as progress before the user has entered anything.
  const WORK_STEPS = STEP_LABELS.length - 1;
  const pct = Math.round((Math.max(maxStep - 1, 0) / WORK_STEPS) * 100);

  if (mode === 'multi') {
    const onWorkpiece = multiStage === 'workpiece';
    // childIds already holds the current workpiece; it isn't done yet.
    const inList = postId !== null && childIds.includes(postId);
    const doneCount = childIds.length - (onWorkpiece && inList ? 1 : 0);

    return (
      <div className="rounded-xl border border-border-strong bg-card p-4">
        <div className={cn(FIELD_LABEL, 'mb-1')}>Project progress</div>
        <p className="mb-2 text-[11px] text-muted-foreground">
          Workpiece {workpieceIndex} of{' '}
          {Math.max(childIds.length, workpieceIndex)}
        </p>
        <div className="flex flex-col gap-1.5">
          <StepNavigation frozen={!onWorkpiece} />
          <RailRow
            number={STEP_LABELS.length}
            label="Workpieces"
            note={`${doneCount} done`}
            isNow={multiStage === 'interstitial'}
            isDone={multiStage === 'review'}
          />
          <RailRow
            number={STEP_LABELS.length + 1}
            label="Submit"
            note={multiStage === 'review' ? 'now' : ''}
            isNow={multiStage === 'review'}
            isDone={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border-strong bg-card p-4">
      <div className={cn(FIELD_LABEL, 'mb-2')}>Project progress</div>
      <StepNavigation />
      <div className="mt-4 flex items-center gap-2.5">
        <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-base ease-out-strong"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {pct}% complete
        </span>
      </div>
    </div>
  );
}
